/* Article enhancements: scrollable tables, heading ids, table of contents, reading progress.
   The article is fully readable without this file; nothing here moves or hides content. */
(() => {
  'use strict';

  const article = document.querySelector('[data-article]');
  const body = article ? article.querySelector('[data-article-body]') : null;
  if (!article || !body) return;

  /* Headings inside these blocks are not part of the article outline */
  const OUTSIDE_OUTLINE = '.callout, .takeaway, blockquote, .footnotes, table, figure';
  const MIN_TOC_HEADINGS = 3;

  /* ---------- Tables: wrap bare tables so they scroll inside the column ---------- */
  function wrapTables() {
    body.querySelectorAll('table').forEach((table) => {
      if (table.parentElement && table.parentElement.classList.contains('table-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'table-wrap';
      wrap.tabIndex = 0;
      const caption = table.querySelector('caption');
      const label = caption ? caption.textContent.trim() : article.dataset.tableLabel || '';
      if (label) {
        wrap.setAttribute('role', 'region');
        wrap.setAttribute('aria-label', label);
      }
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* ---------- Heading ids: keep every existing unique id, fill gaps, fix duplicates ---------- */
  function slugify(text, andWord) {
    return text
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ` ${andWord} `)
      .replace(/[\u2018\u2019\u201C\u201D"'`]/g, '')
      .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .trim()
      .replace(/[\s-]+/g, '-');
  }

  function ensureHeadingIds(headings) {
    const lang = (document.documentElement.lang || '').toLowerCase();
    const andWord = lang.startsWith('ms') ? 'dan' : 'and';

    /* The first element in document order owns an id; anything later with the same id is a duplicate. */
    const owners = new Map();
    document.querySelectorAll('[id]').forEach((element) => {
      if (element.id && !owners.has(element.id)) owners.set(element.id, element);
    });
    const taken = new Set(owners.keys());
    const generated = new Set();

    headings.forEach((heading, index) => {
      const existing = heading.getAttribute('id');
      if (existing && owners.get(existing) === heading) return;

      const base = slugify(heading.textContent || '', andWord) || `section-${index + 1}`;
      let candidate = base;
      let suffix = 2;
      while (taken.has(candidate)) {
        candidate = `${base}-${suffix}`;
        suffix += 1;
      }
      heading.id = candidate;
      taken.add(candidate);
      generated.add(candidate);
    });

    return generated;
  }

  /* ---------- Table of contents ---------- */
  function buildToc(headings) {
    const nav = article.querySelector('[data-toc]');
    const list = nav ? nav.querySelector('[data-toc-list]') : null;
    if (!nav || !list || headings.length < MIN_TOC_HEADINGS) return;

    let lastTopItem = null;
    let subList = null;

    headings.forEach((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = heading.textContent.replace(/\s+/g, ' ').trim();
      item.appendChild(link);

      if (heading.tagName === 'H3' && lastTopItem) {
        if (!subList) {
          subList = document.createElement('ol');
          subList.setAttribute('role', 'list');
          lastTopItem.appendChild(subList);
        }
        subList.appendChild(item);
      } else {
        list.appendChild(item);
        lastTopItem = item;
        subList = null;
      }
    });

    nav.hidden = false;
  }

  /* ---------- Reading progress (decorative; aria-hidden in markup) ---------- */
  function initProgress() {
    const bar = document.querySelector('[data-article-progress]');
    const fill = bar ? bar.firstElementChild : null;
    if (!bar || !fill) return;
    bar.hidden = false;

    let queued = false;
    const update = () => {
      queued = false;
      const rect = body.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      const ratio = distance > 0 ? -rect.top / distance : rect.top <= 0 ? 1 : 0;
      fill.style.transform = `scaleX(${Math.min(Math.max(ratio, 0), 1)})`;
    };
    const request = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  /* ---------- Init ---------- */
  wrapTables();

  const headings = Array.from(body.querySelectorAll('h2, h3')).filter(
    (heading) => !heading.closest(OUTSIDE_OUTLINE)
  );
  const generated = ensureHeadingIds(headings);
  buildToc(headings);
  initProgress();

  /* A shared link may point at an id that only exists now; jump to it once. */
  const hash = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';
  if (hash && generated.has(hash)) {
    const target = document.getElementById(hash);
    if (target) target.scrollIntoView();
  }

})();
